import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Table, Container, Row, Col, ButtonGroup, Button, Badge, Spinner } from "reactstrap";
import { getUserListPlans } from "./_requests";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { formatDate } from "../../helpers/FunctionHelpers";
import Pagination from "../../Components/Common/Pagination";

const UserPlanSubscriptionList = () => {
  const { id } = useParams();
  const [getUserlist, setUserListPlans] = useState<Array<any>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const perPageData = 10;

  const fetchSubscriptions = useCallback(() => {
    setLoading(true);
    getUserListPlans(id, { filter, page: currentPage, limit: perPageData })
      .then((res: any) => {
        if (res.status === true) {
          setUserListPlans(res.data || []);
          setTotalCount(res.totalCount || 0);
        } else {
          setUserListPlans([]);
          setTotalCount(0);
        }
      })
      .catch((err) => {
        console.error("Error fetching subscriptions:", err);
        setUserListPlans([]);
        setTotalCount(0);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, filter, currentPage]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const handleFilterChange = (newFilter: string) => {
    setFilter(newFilter);
    setCurrentPage(1); // Reset to first page on filter change
  };

  return (
    <div className="">
      <Container fluid>
        <Row className="mb-3 align-items-center">
          <Col md={6}>
            <h5 className="mb-0">Subscriptions ({totalCount})</h5>
          </Col>
          <Col md={6} className="text-md-end">
            <ButtonGroup size="sm">
              <Button
                color="primary"
                outline
                active={filter === "all"}
                onClick={() => handleFilterChange("all")}
              >
                All
              </Button>
              <Button
                color="success"
                outline
                active={filter === "active"}
                onClick={() => handleFilterChange("active")}
              >
                Active
              </Button>
              <Button
                color="danger"
                outline
                active={filter === "expired"}
                onClick={() => handleFilterChange("expired")}
              >
                Expired
              </Button>
            </ButtonGroup>
          </Col>
        </Row>

        <Row>
          <Col lg={12}>
            <div className="table-responsive">
              <Table bordered hover>
                <thead className="table-light text-primary fw-bold">
                  <tr>
                    <th>#</th>
                    <th>Plan Details</th>
                    <th style={{ minWidth: "160px" }}>Date</th>
                    <th>Status</th>
                    <th>User Info</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="text-center py-5">
                        <Spinner color="primary" />
                      </td>
                    </tr>
                  ) : getUserlist?.length > 0 ? (
                    getUserlist.map((plan, index) => (
                      <tr key={plan.id}>
                        <td>{(currentPage - 1) * perPageData + index + 1}</td>
                        <td>
                          <h6 className="text-primary mb-1">{plan?.subscription?.name || "N/A"}</h6>
                          <small className="d-block mb-1">
                            {plan?.type_name || "N/A"} - <span className="text-muted">({plan?.slot_type_name || "N/A"})</span>
                          </small>
                          <div className="mt-1">
                            <span className="text-success fw-bold small me-2">{plan?.payment_id || "N/A"}</span>
                            <Badge color={plan?.payment_status === "success" ? "success" : "warning"} className="text-uppercase" style={{ fontSize: "10px" }}>
                              {plan?.payment_status}
                            </Badge>
                          </div>
                        </td>
                        <td className="text-nowrap">
                          <div className="small">
                            <i className="ri-calendar-event-line text-primary me-1"></i>
                            {formatDate(plan?.start_date)}
                            <br />
                            <span className="text-muted ms-3">to</span>
                            <br />
                            <i className="ri-calendar-check-line text-success me-1"></i>
                            {plan?.end_date ? formatDate(plan?.end_date) : "N/A"}
                          </div>
                        </td>
                        <td>
                          {plan?.is_active === 1 ? (
                            <Badge color="success" pill>Active</Badge>
                          ) : (
                            <Badge color="danger" pill>Expired</Badge>
                          )}
                        </td>
                        <td>
                          <div className="fw-bold text-dark mb-1">{plan?.user?.name}</div>
                          <div className="small text-muted">
                            <span className="fw-medium text-primary d-block mb-1">{plan?.addressbook?.address_type || "Address"}</span>
                            {plan?.shipping_address || 
                              `${plan?.addressbook?.address || ""}${plan?.addressbook?.city ? ", " + plan?.addressbook?.city : ""}${plan?.addressbook?.state ? ", " + plan?.addressbook?.state : ""}`}
                          </div>
                          {(plan?.addressbook?.latitude || plan?.addressbook?.longitude) && (
                            <div className="text-info small mt-1" style={{ fontSize: "11px" }}>
                              <i className="ri-map-pin-fill me-1"></i>
                              Lat: {plan.addressbook.latitude}, Long: {plan.addressbook.longitude}
                            </div>
                          )}
                        </td>
                        <td>
                          <Table size="sm" bordered className="mb-0 small">
                            <thead className="table-light">
                              <tr>
                                <th>Product</th>
                                <th>Category</th>
                                <th>Variant</th>
                                <th>Validity</th>
                              </tr>
                            </thead>
                            <tbody>
                              {plan.details?.map((detail: any) => (
                                <tr key={detail.id}>
                                  <td><b>{detail?.product?.name || "N/A"}</b></td>
                                  <td>{detail?.category?.name || "N/A"}</td>
                                  <td>{detail?.variant_value} {detail?.variant?.name || ""}</td>
                                  <td>{detail?.validity} {detail?.validity_type}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center fw-bold py-4">
                        No data found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Col>
        </Row>

        {totalCount > perPageData && (
          <Pagination
            data={{ length: totalCount }}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            perPageData={perPageData}
          />
        )}
      </Container>
    </div>
  );
};

export default UserPlanSubscriptionList;
